import { useEffect, useState } from 'react'
import { User, Award, BookOpen, Megaphone, Settings, X, Save, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'

interface ProfileProps {
  user: any
}

export default function Profile({ user }: ProfileProps) {
  const [profile, setProfile] = useState<any>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [message, setMessage] = useState({ type: '', text: '' })

  useEffect(() => {
    if (!user) {
      window.location.href = '/auth'
      return
    }
    fetchProfile()
  }, [user])

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle()

    setProfile(data)
    setEditForm({
      full_name: data?.full_name || '',
      email: data?.email || user.email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
  }

  const handleEdit = () => {
    setIsEditing(true)
    setMessage({ type: '', text: '' })
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditForm({
      full_name: profile?.full_name || '',
      email: profile?.email || user.email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setMessage({ type: '', text: '' })
  }

  const handleSave = async () => {
    setIsLoading(true)
    setMessage({ type: '', text: '' })

    try {
      // Validaciones
      if (!editForm.full_name.trim()) {
        throw new Error('El nombre completo es obligatorio')
      }

      // Actualizar datos del perfil
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: editForm.full_name.trim(),
          email: editForm.email,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)

      if (profileError) throw profileError

      // Si el usuario quiere cambiar la contraseña
      if (editForm.newPassword) {
        if (editForm.newPassword !== editForm.confirmPassword) {
          throw new Error('Las contraseñas no coinciden')
        }

        if (editForm.newPassword.length < 6) {
          throw new Error('La nueva contraseña debe tener al menos 6 caracteres')
        }

        // Actualizar contraseña usando el método correcto
        const { error: passwordError } = await supabase.auth.updateUser({
          password: editForm.newPassword
        })

        if (passwordError) throw passwordError
      }

      // Actualizar email si cambió
      if (editForm.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: editForm.email
        })

        if (emailError) throw emailError
      }

      setMessage({ type: 'success', text: 'Perfil actualizado exitosamente' })
      setIsEditing(false)
      
      // Actualizar datos del perfil
      await fetchProfile()
      
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message })
    } finally {
      setIsLoading(false)
    }
  }

  if (!user || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-education"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header del perfil */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-education rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {profile.full_name?.[0] || user.email?.[0].toUpperCase()}
                </div>
                <div>
                  <h1 className="font-heading font-bold text-2xl text-neutral-800 mb-1">
                    {profile.full_name || 'Usuario'}
                  </h1>
                  <p className="text-neutral-600 mb-2">{user.email}</p>
                  {profile.location && (
                    <p className="text-sm text-neutral-500">{profile.location}</p>
                  )}
                </div>
              </div>
              {!isEditing ? (
                <button 
                  onClick={handleEdit}
                  className="flex items-center space-x-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50"
                >
                  <Settings className="w-4 h-4" />
                  <span>Editar</span>
                </button>
              ) : (
                <div className="flex space-x-2">
                  <button 
                    onClick={handleSave}
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{isLoading ? 'Guardando...' : 'Guardar'}</span>
                  </button>
                  <button 
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="flex items-center space-x-2 px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancelar</span>
                  </button>
                </div>
              )}
            </div>

            {/* Formulario de edición */}
            {isEditing && (
              <div className="mt-6 pt-6 border-t border-neutral-200">
                <h3 className="text-lg font-semibold mb-4">Editar Perfil</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Nombre Completo
                    </label>
                    <input
                      type="text"
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                {/* Cambio de contraseña */}
                <div className="mt-6">
                  <h4 className="text-md font-medium mb-4">Cambiar Contraseña (opcional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Nueva Contraseña
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={editForm.newPassword}
                          onChange={(e) => setEditForm({...editForm, newPassword: e.target.value})}
                          className="w-full px-3 py-2 pr-10 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                          placeholder="Nueva contraseña"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 hover:text-neutral-700"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Confirmar Nueva Contraseña
                      </label>
                      <input
                        type="password"
                        value={editForm.confirmPassword}
                        onChange={(e) => setEditForm({...editForm, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                        placeholder="Confirmar contraseña"
                      />
                    </div>
                  </div>
                </div>

                {/* Mensaje de estado */}
                {message.text && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {message.text}
                  </div>
                )}
              </div>
            )}

            {/* Progreso */}
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="font-bold text-2xl text-education mb-1">
                    {profile.education_points || 0}
                  </div>
                  <p className="text-sm text-neutral-600">Puntos Educación</p>
                </div>
                <div className="text-center">
                  <div className="font-bold text-2xl text-action mb-1">
                    {profile.participation_points || 0}
                  </div>
                  <p className="text-sm text-neutral-600">Puntos Participación</p>
                </div>
                <div className="text-center">
                  <div className="font-bold text-2xl text-community mb-1">
                    {profile.change_score || 0}
                  </div>
                  <p className="text-sm text-neutral-600">Change Score</p>
                </div>
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="font-semibold text-lg mb-4 flex items-center space-x-2">
              <Award className="w-5 h-5 text-community" />
              <span>Mis Badges</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 border border-neutral-200 rounded-lg">
                <div className="text-3xl mb-2">🎓</div>
                <p className="text-sm font-medium">Primer Curso</p>
              </div>
              <div className="text-center p-4 border border-neutral-200 rounded-lg opacity-50">
                <div className="text-3xl mb-2">📖</div>
                <p className="text-sm font-medium">Lector Voraz</p>
              </div>
              <div className="text-center p-4 border border-neutral-200 rounded-lg">
                <div className="text-3xl mb-2">✊</div>
                <p className="text-sm font-medium">Primer Activista</p>
              </div>
              <div className="text-center p-4 border border-neutral-200 rounded-lg opacity-50">
                <div className="text-3xl mb-2">🎯</div>
                <p className="text-sm font-medium">Learning-Activist</p>
              </div>
            </div>
          </div>

          {/* Actividad reciente */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-education" />
                <span>Cursos Recientes</span>
              </h2>
              <p className="text-neutral-600 text-sm">Aún no has completado cursos</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="font-semibold text-lg mb-4 flex items-center space-x-2">
                <Megaphone className="w-5 h-5 text-action" />
                <span>Campañas Firmadas</span>
              </h2>
              <p className="text-neutral-600 text-sm">Aún no has firmado campañas</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
